import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PageHeader, Card, UnvButton } from 'components'
import { Icon } from 'components/Icons'

import styles from './ApplicantHome.module.scss'

/**
 * P1-A1: домашняя страница абитуриента.
 * Раньше роль applicant редиректилась на /home/ (MainPage), который рассчитан
 * на сотрудников и в реальности показывал пустой UI. Теперь roleRedirect.js
 * ведёт на /applicant-home/ — эта страница.
 *
 * UI намеренно простой: 3 карточки-ярлыка для основных действий абитуриента.
 * Реальные модули (приёмная кампания, проверка статуса, загрузка документов)
 * подключатся позже — это заглушка с правильным каркасом.
 */
const APPLICANT_CARDS = [
  {
    key: 'apply',
    icon: 'plus',
    titleKey: 'applicantHome.apply.title',
    titleFallback: 'Подать заявление',
    descKey: 'applicantHome.apply.desc',
    descFallback: 'Заполнить анкету абитуриента и выбрать направление подготовки.',
    to: '/admission/',
    ctaKey: 'applicantHome.apply.cta',
    ctaFallback: 'Перейти к подаче',
  },
  {
    key: 'status',
    icon: 'eye',
    titleKey: 'applicantHome.status.title',
    titleFallback: 'Статус приёма',
    descKey: 'applicantHome.status.desc',
    descFallback: 'Отслеживайте этапы рассмотрения заявления и приёмной кампании.',
    to: '/statements/',
    ctaKey: 'applicantHome.status.cta',
    ctaFallback: 'Открыть статус',
  },
  {
    key: 'docs',
    icon: 'file',
    titleKey: 'applicantHome.docs.title',
    titleFallback: 'Документы',
    descKey: 'applicantHome.docs.desc',
    descFallback: 'Загрузите аттестат, паспорт и другие документы для приёмной комиссии.',
    to: '/personalcard/',
    ctaKey: 'applicantHome.docs.cta',
    ctaFallback: 'Мои документы',
  },
]

const ApplicantHome = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()

  useEffect(() => {
    document.title = `${t('applicantHome.title', 'Абитуриент')} — ЧУ`
  }, [t])

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('applicantHome.title', 'Абитуриент')}
        subtitle={t(
          'applicantHome.subtitle',
          'Подача документов, отслеживание статуса и связь с приёмной комиссией.',
        )}
      />

      <div className={styles.grid}>
        {APPLICANT_CARDS.map((c) => (
          <Card
            key={c.key}
            interactive
            padding="lg"
            className={styles.card}
            onClick={() => navigate(c.to)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                navigate(c.to)
              }
            }}
          >
            <div className={styles.cardIcon} aria-hidden="true">
              <Icon name={c.icon} size={28} />
            </div>
            <h3 className={styles.cardTitle}>
              {t(c.titleKey, c.titleFallback)}
            </h3>
            <p className={styles.cardDesc}>
              {t(c.descKey, c.descFallback)}
            </p>
            <div className={styles.cardCta}>
              <UnvButton
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  // карточка целиком кликабельна — но кнопка тоже навигирует
                  // и не должна стрелять onClick карточки дважды.
                  e.stopPropagation()
                  navigate(c.to)
                }}
              >
                {t(c.ctaKey, c.ctaFallback)}
              </UnvButton>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default ApplicantHome
